import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Separator } from "../components/ui/separator";
import Navigation from "../components/layout/Navigation";
import Footer from "../components/layout/Footer";
import { 
  Calendar, Clock, ArrowRight, BookOpen, Heart, 
  Newspaper, GraduationCap, Palette, ChevronLeft,
  Eye, Share2, Tag
} from "lucide-react";
import { API } from "../apiConfig";


// Category config
const CATEGORIES = {
  all: { label: "All Articles", icon: BookOpen, color: "bg-primary" },
  news: { label: "News & Updates", icon: Newspaper, color: "bg-blue-500" },
  education: { label: "Education", icon: GraduationCap, color: "bg-green-500" },
  culture: { label: "Culture", icon: Palette, color: "bg-purple-500" },
  art4health: { label: "ART4HEALTH", icon: Heart, color: "bg-red-500" }
};

// Article List Page
const MediaPage = () => {
  const [articles, setArticles] = useState([]);
  const [featuredArticles, setFeaturedArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("all");

  useEffect(() => {
    fetchArticles();
  }, []);

  const fetchArticles = async () => {
    try {
      const [allRes, featuredRes] = await Promise.all([
        axios.get(`${API}/articles`),
        axios.get(`${API}/articles/featured`)
      ]);
      setArticles(allRes.data);
      setFeaturedArticles(featuredRes.data);
    } catch (error) {
      console.error("Error fetching articles:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const filteredArticles = activeCategory === "all" 
    ? articles 
    : articles.filter(a => a.category === activeCategory);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <div className="pt-32 pb-24 px-6 md:px-12">
          <div className="max-w-6xl mx-auto">
            <div className="animate-pulse space-y-8">
              <div className="h-12 bg-muted rounded w-1/3" />
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3, 4, 5, 6].map(i => (
                  <div key={i} className="h-80 bg-muted rounded-2xl" />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="media-page">
      <Navigation />

      {/* Hero Section */}
      <section className="pt-28 pb-12 px-6 md:px-12 bg-gradient-to-b from-primary/5 to-transparent">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-5xl font-medium mb-4">
              Media & Insights
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Stories, education, and news from the world of Aboriginal art and culture
            </p>
          </motion.div>
        </div>
      </section>

      {/* Featured Article */}
      {featuredArticles.length > 0 && (
        <section className="px-6 md:px-12 pb-12">
          <div className="max-w-6xl mx-auto">
            <Link to={`/media/${featuredArticles[0].slug}`}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="relative rounded-3xl overflow-hidden group cursor-pointer"
              >
                <div className="aspect-[21/9] md:aspect-[3/1]">
                  <img 
                    src={featuredArticles[0].featured_image} 
                    alt={featuredArticles[0].title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
                  <Badge className={`mb-3 ${CATEGORIES[featuredArticles[0].category]?.color || 'bg-primary'}`}>
                    {CATEGORIES[featuredArticles[0].category]?.label || 'Article'}
                  </Badge>
                  <h2 className="text-2xl md:text-4xl font-medium text-white mb-3 group-hover:text-primary transition-colors">
                    {featuredArticles[0].title}
                  </h2>
                  <p className="text-white/80 text-sm md:text-base max-w-2xl line-clamp-2 mb-4">
                    {featuredArticles[0].excerpt}
                  </p>
                  <div className="flex items-center gap-4 text-white/60 text-sm">
                    <span className="flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      {formatDate(featuredArticles[0].created_at)}
                    </span>
                    <span className="flex items-center gap-1">
                      <Eye className="h-4 w-4" />
                      {featuredArticles[0].views || 0} views
                    </span>
                  </div>
                </div>
              </motion.div>
            </Link>
          </div>
        </section>
      )}

      {/* Category Filter */}
      <section className="px-6 md:px-12 pb-8">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-wrap gap-2">
            {Object.entries(CATEGORIES).map(([key, { label, icon: Icon }]) => (
              <Button
                key={key}
                variant={activeCategory === key ? "default" : "outline"}
                className="rounded-full"
                onClick={() => setActiveCategory(key)}
              >
                <Icon className="h-4 w-4 mr-2" />
                {label}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="px-6 md:px-12 pb-24">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredArticles.map((article, index) => (
              <motion.div
                key={article.article_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Link to={`/media/${article.slug}`}>
                  <Card className="rounded-2xl overflow-hidden group cursor-pointer h-full hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                    <div className="aspect-[4/3] overflow-hidden">
                      <img 
                        src={article.featured_image} 
                        alt={article.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                    </div>
                    <CardContent className="p-5">
                      <Badge variant="outline" className="mb-3 text-xs">
                        {CATEGORIES[article.category]?.label || article.category}
                      </Badge>
                      <h3 className="font-medium text-lg mb-2 line-clamp-2 group-hover:text-primary transition-colors">
                        {article.title}
                      </h3>
                      <p className="text-sm text-muted-foreground line-clamp-2 mb-4">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {formatDate(article.created_at)}
                        </span>
                        <span className="flex items-center gap-1 text-primary font-medium group-hover:gap-2 transition-all">
                          Read More <ArrowRight className="h-3 w-3" />
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </div>

          {filteredArticles.length === 0 && (
            <div className="text-center py-12">
              <BookOpen className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
              <p className="text-muted-foreground">No articles found in this category</p>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

// Single Article Page
export const ArticlePage = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchArticle();
  }, [slug]);

  const fetchArticle = async () => {
    try {
      const [articleRes, allRes] = await Promise.all([
        axios.get(`${API}/articles/${slug}`),
        axios.get(`${API}/articles`)
      ]);
      setArticle(articleRes.data);
      // Get related articles (same category, excluding current)
      setRelatedArticles(
        allRes.data
          .filter(a => a.slug !== slug && a.category === articleRes.data.category)
          .slice(0, 3)
      );
    } catch (error) {
      console.error("Error fetching article:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const shareArticle = () => {
    if (navigator.share) {
      navigator.share({
        title: article.title,
        text: article.excerpt,
        url: window.location.href
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <div className="pt-32 pb-24 px-6 md:px-12">
          <div className="max-w-3xl mx-auto animate-pulse space-y-6">
            <div className="h-8 bg-muted rounded w-3/4" />
            <div className="h-64 bg-muted rounded-2xl" />
            <div className="space-y-3">
              <div className="h-4 bg-muted rounded" />
              <div className="h-4 bg-muted rounded w-5/6" />
              <div className="h-4 bg-muted rounded w-4/6" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <div className="pt-32 pb-24 px-6 md:px-12 text-center">
          <h1 className="text-2xl font-medium mb-4">Article Not Found</h1>
          <Link to="/media">
            <Button className="rounded-full">
              <ChevronLeft className="h-4 w-4 mr-2" />
              Back to Media
            </Button>
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="article-page">
      <Navigation />

      {/* Hero Image */}
      <section className="pt-24">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <Link to="/media" className="inline-flex items-center text-sm text-muted-foreground hover:text-primary mb-6">
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back to Media
          </Link>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Badge className={`mb-4 ${CATEGORIES[article.category]?.color || 'bg-primary'}`}>
              {CATEGORIES[article.category]?.label || article.category}
            </Badge>
            <h1 className="text-3xl md:text-5xl font-medium mb-6 leading-tight">
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-8">
              <span className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                {formatDate(article.created_at)}
              </span>
              <span className="flex items-center gap-1">
                <Eye className="h-4 w-4" />
                {article.views || 0} views
              </span>
              <span>By {article.author}</span>
              <Button variant="ghost" size="sm" className="rounded-full" onClick={shareArticle}>
                <Share2 className="h-4 w-4 mr-2" />
                Share
              </Button>
            </div>
          </motion.div>
        </div>

        {article.featured_image && (
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            className="max-w-6xl mx-auto px-6 md:px-12 mb-12"
          >
            <div className="aspect-[21/9] rounded-2xl overflow-hidden">
              <img 
                src={article.featured_image} 
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>
          </motion.div>
        )}
      </section>

      {/* Article Content */}
      <section className="px-6 md:px-12 pb-12">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="prose prose-lg dark:prose-invert max-w-none"
          >
            {/* Render markdown content */}
            <div 
              className="article-content"
              dangerouslySetInnerHTML={{ 
                __html: article.content
                  .replace(/### (.*)/g, '<h3>$1</h3>')
                  .replace(/#### (.*)/g, '<h4>$1</h4>')
                  .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                  .replace(/\*(.*?)\*/g, '<em>$1</em>')
                  .replace(/- (.*)/g, '<li>$1</li>')
                  .replace(/---/g, '<hr />')
                  .replace(/\n\n/g, '</p><p>')
                  .replace(/^/, '<p>')
                  .replace(/$/, '</p>')
              }}
            />
          </motion.div>

          {/* Tags */}
          {article.tags && article.tags.length > 0 && (
            <div className="mt-12 pt-8 border-t">
              <div className="flex items-center gap-2 flex-wrap">
                <Tag className="h-4 w-4 text-muted-foreground" />
                {article.tags.map((tag, idx) => (
                  <Badge key={idx} variant="outline" className="capitalize">
                    {tag}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Related Articles */}
      {relatedArticles.length > 0 && (
        <section className="px-6 md:px-12 pb-24 bg-muted/30">
          <div className="max-w-6xl mx-auto py-12">
            <h2 className="text-2xl font-medium mb-8">Related Articles</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedArticles.map((related) => (
                <Link key={related.article_id} to={`/media/${related.slug}`}>
                  <Card className="rounded-2xl overflow-hidden group cursor-pointer h-full hover:shadow-lg transition-all">
                    <div className="aspect-[4/3] overflow-hidden">
                      <img 
                        src={related.featured_image} 
                        alt={related.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                    </div>
                    <CardContent className="p-4">
                      <h3 className="font-medium line-clamp-2 group-hover:text-primary transition-colors">
                        {related.title}
                      </h3>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="px-6 md:px-12 pb-24">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl font-medium mb-4">Support Aboriginal Art & Culture</h2>
          <p className="text-muted-foreground mb-6">
            Every purchase supports Aboriginal artists and communities. Explore our collection of authentic Aboriginal artworks.
          </p>
          <Link to="/products">
            <Button size="lg" className="rounded-full">
              Browse Collection
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MediaPage;
